import React, { useEffect, useState } from 'react';
import { BookmarkPlus, FileText, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

export const openQuoteDoc = async (api, f) => {
  try {
    const r = await api().get(`/quote-docs/${f.id}/file`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank');
  } catch { toast.error('Could not open file'); }
};

export const QuoteDocUpload = ({ api, refType, refId, onDone, testId }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append('ref_type', refType); fd.append('ref_id', refId); fd.append('file', file);
    try { const r = await api().post('/quote-docs', fd); toast.success(`${file.name} attached`); onDone(r.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <label className={`inline-flex items-center gap-1 text-xs px-2 h-8 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 ${busy ? 'opacity-50' : ''}`} data-testid={testId}>
      <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : 'Attach quote'}
      <input type="file" className="hidden" accept=".pdf,image/*,.xlsx,.docx,.msg,.eml" onChange={pick} disabled={busy} />
    </label>
  );
};

// Vendor quote #, quote date and quote file(s) on the PO header.
export const PoQuoteFields = ({ api, form, setForm, po, editable, onChanged }) => {
  const age = form.quote_date ? Math.floor((Date.now() - new Date(form.quote_date).getTime()) / 86400000) : null;
  return (
    <div className="md:col-span-3 grid md:grid-cols-4 gap-3 items-end" data-testid="po-quote-fields">
      <div><Label>Vendor Quote #</Label><Input value={form.vendor_quote_number || ''} onChange={(e) => setForm({ ...form, vendor_quote_number: e.target.value })} disabled={!editable} maxLength={80} className="rounded-sm mono" data-testid="po-quote-number" /></div>
      <div><Label>Quote Date</Label><Input type="date" value={form.quote_date || ''} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="po-quote-date" /></div>
      <div className="md:col-span-2 flex items-center gap-2 flex-wrap pb-1">
        {po ? <QuoteDocUpload api={api} refType="po" refId={po.id} onDone={onChanged} testId="po-quote-upload" /> : <span className="text-[11px] text-slate-500">Save the PO to attach the quote file.</span>}
        {(po?.quote_files || []).map(f => (
          <button key={f.id} type="button" onClick={() => openQuoteDoc(api, f)} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid={`po-quote-file-${f.id}`}><FileText className="w-3.5 h-3.5" />{f.filename}</button>
        ))}
        {age !== null && age > 30 && <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid="po-quote-stale"><AlertTriangle className="w-3 h-3" /> Quote is {age} days old, so pricing may have changed</span>}
      </div>
    </div>
  );
};

// Ship To: pick a company / address-book / customer / vendor address or type a new one (optionally saved).
export const ShipToPicker = ({ api, value, onChange, disabled }) => {
  const [opts, setOpts] = useState([]);
  const [label, setLabel] = useState('');
  const load = () => api().get('/ship-to-options').then(r => setOpts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = [...new Set(opts.map(o => o.group))];
  const match = opts.findIndex(o => o.address === value);
  const save = async () => {
    try { await api().post('/ship-addresses', { label, address: value }); toast.success('Saved to address book'); setLabel(''); load(); }
    catch (e) { toast.error('Add a label and an address first'); }
  };
  return (
    <div className="md:col-span-3 space-y-1.5" data-testid="po-ship-to-picker">
      <Label>Ship To</Label>
      <select value={match >= 0 ? String(match) : value ? 'custom' : ''} disabled={disabled} onChange={(e) => { const v = e.target.value; if (v === '' ) onChange(''); else if (v !== 'custom') onChange(opts[Number(v)].address); }}
        className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm bg-white" data-testid="po-ship-to-select">
        <option value="">Company address (default)</option>
        {groups.map(g => <optgroup key={g} label={g}>{opts.map((o, i) => o.group === g && <option key={i} value={i}>{o.label}</option>)}</optgroup>)}
        <option value="custom">New / custom address…</option>
      </select>
      <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder="Type a new address on behalf of a customer or vendor" className="rounded-sm text-sm" data-testid="po-ship-to" />
      {!disabled && value && match < 0 && (
        <div className="flex gap-2 items-center">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label, e.g. John's farm – Lethbridge" maxLength={80} className="h-8 rounded-sm text-sm" data-testid="po-ship-to-label" />
          <Button type="button" size="sm" variant="outline" onClick={save} disabled={!label.trim()} className="rounded-sm h-8 shrink-0" data-testid="po-ship-to-save"><BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save to address book</Button>
        </div>
      )}
    </div>
  );
};
